<template>
  <div class="panel">
    <div class="panel-title">Force refresh WebUI</div>
    <div class="panel-subtitle">
      Force le rechargement de la version WebUI pour une société.
    </div>

    <div class="panel-grid">
      <hit-select
        class="min-w-[32rem] w-full"
        :label="'Société'"
        :options="companyOptions"
        :value="selectedSchemaId"
        item-id-property="id"
        :label-fields="['label']"
        clearable
        @change="onCompanyChange"
      />
    </div>

    <div class="mt-4 flex gap-2">
      <hit-button
        :label="running ? 'Envoi en cours…' : 'Forcer le refresh'"
        color="accent"
        prefix-icon="refresh"
        :disabled="running || !canRun"
        @click="run"
      />
      <hit-button
        :label="'Réinitialiser'"
        color="button"
        prefix-icon="reset_changes"
        :disabled="running"
        @click="resetForm"
      />
    </div>

    <div v-if="result" class="result mt-4">
      <div class="result-title" :class="{ok: result.ok, ko: !result.ok}">
        {{ result.ok ? 'Succès' : 'Erreur' }}
      </div>
      <pre class="result-pre">{{ resultText }}</pre>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';

export default defineComponent({
  name: 'ScriptForceRefreshWebui',
  components: {HitButton, HitSelect},
  data() {
    return {
      companyOptions: [],
      selectedSchemaId: null,
      running: false,
      result: null,
    };
  },
  computed: {
    canRun() {
      return !!this.selectedSchemaId;
    },
    resultText() {
      try {
        return JSON.stringify(this.result, null, 2);
      } catch {
        return String(this.result);
      }
    },
  },
  async mounted() {
    await this.loadCompanyOptions();
  },
  methods: {
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({limit: 500});
        const rows = res?.rows || [];
        this.companyOptions = rows.map((r) => {
          const label = [r.designation, r.vat_no].filter(Boolean).join(' – ');
          return {id: r.schema_id || r.id, label: label || r.id};
        });
      } catch {
        this.companyOptions = [];
      }
    },
    onCompanyChange(value) {
      this.selectedSchemaId = value;
      this.result = null;
    },
    resetForm() {
      this.selectedSchemaId = null;
      this.result = null;
    },
    async run() {
      if (!this.canRun) return;
      this.running = true;
      this.result = null;
      try {
        const res = await adminApi.forceRefreshWebuiVersion(
          this.selectedSchemaId,
        );
        this.result = res;
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.running = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.panel {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.82);
  padding: 0.9rem;
}

.panel-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
  margin-bottom: 0.15rem;
}

.panel-subtitle {
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
  line-height: 1.25rem;
  margin-bottom: 0.85rem;
}

.panel-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.result-title {
  font-weight: 700;
}
.result-title.ok {
  color: #03695a;
}
.result-title.ko {
  color: #b91c1c;
}
.result-pre {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.15rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.6rem 0.65rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
